// Zoho Embedded App SDK: https://live.zwidgets.com/js-sdk/1.1/ZohoEmbededAppSDK.min.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getLogRecords,
  searchLogRecords,
  deleteLogRecord,
  buildCriteria,
  retryWebhook,
} from "../../../APICalling/logApi";
import { getAllSetting } from "../../../APICalling/settingApi";
import {
  normaliseLog,
  fmtDate,
  fmtTime,
  fmtDateKey,
  STATUS_COLORS,
  DEFAULT_STATUS,
} from "./logHelpers";

// ── Helpers ──

// Possible values of From/To platform fields on Shopify_Logs records
const PLATFORM_OPTIONS = ["Shopify", "Zoho CRM", "Other"];
const STATUS_OPTIONS = ["Failed", "Processing", "Success", "Partial", "Retrying", "Skipped"];

/** Sort comparator */
const compare = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
};

// ── Chip colour maps ──
// STATUS_COLORS / DEFAULT_STATUS now live in ./logHelpers (shared with Journey).

const MODULE_COLORS = {
  Contacts:        { bg: "#f0fdf4", color: "#15803d" },
  Deals:           { bg: "#eff6ff", color: "#1d4ed8" },
  Products:        { bg: "#fefce8", color: "#a16207" },
  Invoices:        { bg: "#faf5ff", color: "#7e22ce" },
  Sales_Orders:    { bg: "#fff7ed", color: "#c2410c" },
  Items:           { bg: "#fefce8", color: "#a16207" },
};
const DEFAULT_MODULE = { bg: "#f4f4f5", color: "#3f3f46" };

// ── Table columns ──

const COLUMNS = [
  { key: "createdTime",     label: "Date / Time",       sortable: true, width: 150 },
  { key: "name",            label: "Event",             sortable: true, width: 180 },
  { key: "module",          label: "Zoho Module",       sortable: true, width: 120 },
  { key: "shopifyModule",   label: "Shopify Module",    sortable: true, width: 120 },
  { key: "shopifyRecordId", label: "Shopify ID",        sortable: true, width: 120 },
  { key: "shop",            label: "Shop",              sortable: true },
  { key: "status",          label: "Status",            sortable: true, width: 90 },
  { key: "reason",          label: "Reason",            sortable: false },
  { key: "actions",         label: "Action",            sortable: false, width: 140, align: "right" },
];

const PER_PAGE = 200;

// ── Component ──

export default function Logs({ setSnack, orgId, apiKey, dataCenterUrl, dataCenter }) {
  // Data
  const [rawRecords, setRawRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [moreRecords, setMoreRecords] = useState(false);

  // Pagination (server-side)
  const [page, setPage] = useState(1);

  // Server-side filters (trigger re-fetch via criteria)
  const [statusFilter, setStatusFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [fromPlatform, setFromPlatform] = useState("");
  const [toPlatform, setToPlatform] = useState("");

  // Modules configured in settings (includes custom user-added modules)
  const [configuredModules, setConfiguredModules] = useState([]);

  // Client-side filters
  const [searchText, setSearchText] = useState("");
  const [shopFilter, setShopFilter] = useState("");

  // Sorting
  const [sortBy, setSortBy] = useState("createdTime");
  const [sortDir, setSortDir] = useState("desc");

  // Grouping
  const [groupBy, setGroupBy] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Detail dialog
  const [detailLog, setDetailLog] = useState(null);

  // Avoid double-fetch on mount in StrictMode
  const fetchRef = useRef(0);

  // ── Normalised logs ──

  const logs = useMemo(() => {
    const arr = Array.isArray(rawRecords) ? rawRecords : rawRecords ? [rawRecords] : [];
    return arr.map(normaliseLog);
  }, [rawRecords]);

  // ── Fetch ──

  const fetchLogs = useCallback(async () => {
    const ZOHO = window.ZOHO;
    if (!ZOHO?.CRM?.API) {
      setError("Zoho CRM SDK is not available. Make sure you are running inside a Zoho CRM widget.");
      return;
    }

    const fetchId = ++fetchRef.current;
    setLoading(true);
    setError("");

    try {
      const criteria = buildCriteria({
        status: statusFilter,
        module: moduleFilter,
        fromPlatform,
        toPlatform,
      });

      let result;
      if (criteria) {
        result = await searchLogRecords(ZOHO, criteria, { page, perPage: PER_PAGE });
      } else {
        result = await getLogRecords(ZOHO, { page, perPage: PER_PAGE });
      }

      // Only apply if this is still the latest fetch
      if (fetchId !== fetchRef.current) return;

      setRawRecords(result.records);
      setMoreRecords(!!result.info?.more_records);
    } catch (err) {
      if (fetchId !== fetchRef.current) return;
      console.error("[Logs] fetch error:", err);
      setError("Failed to load logs. Please try again.");
      setRawRecords([]);
    }
    setLoading(false);
  }, [page, statusFilter, moduleFilter, fromPlatform, toPlatform]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Fetch modules from user's settings (includes any custom modules they added)
  useEffect(() => {
    if (!orgId || !apiKey || !dataCenterUrl) return;
    let cancelled = false;
    (async () => {
      const settings = await getAllSetting({ orgId, apiKey, dataCenterUrl });
      if (cancelled) return;
      const mods = [
        ...new Set(
          (settings || [])
            .map((s) => {
              const schema = s.settingSchema || s.setting_schema || {};
              return s.moduleApiName || s.module_name || schema.module;
            })
            .filter(Boolean)
        ),
      ].sort();
      setConfiguredModules(mods);
    })();
    return () => { cancelled = true; };
  }, [orgId, apiKey, dataCenterUrl]);

  // ── Derived filter options ──

  const statusOptions = STATUS_OPTIONS;

  // Union of modules from settings + any modules present in current log page
  // (covers legacy logs for modules that have since been removed from settings).
  const moduleOptions = useMemo(() => {
    const fromLogs = logs.map((l) => l.module).filter(Boolean);
    return [...new Set([...configuredModules, ...fromLogs])].sort();
  }, [logs, configuredModules]);

  const shopOptions = useMemo(() =>
    [...new Set(logs.map((l) => l.shop).filter(Boolean))].sort(), [logs]);

  // ── Client-side filter + sort ──

  const filteredLogs = useMemo(() => {
    let list = logs;

    if (shopFilter) list = list.filter((l) => l.shop === shopFilter);

    if (searchText) {
      const q = searchText.toLowerCase();
      list = list.filter((l) =>
        l.name.toLowerCase().includes(q) ||
        l.reason.toLowerCase().includes(q) ||
        l.shopifyRecordId.toLowerCase().includes(q) ||
        l.moduleRecordId.toLowerCase().includes(q) ||
        l.shop.toLowerCase().includes(q) ||
        l.shopifyModule.toLowerCase().includes(q) ||
        l.module.toLowerCase().includes(q) ||
        l.status.toLowerCase().includes(q) ||
        l.settingId.toLowerCase().includes(q)
      );
    }

    // Sort
    return [...list].sort((a, b) => {
      let va = a[sortBy];
      let vb = b[sortBy];
      if (sortBy === "createdTime") {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      }
      const cmp = compare(va, vb);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [logs, shopFilter, searchText, sortBy, sortDir]);

  // ── Groups ──

  const groups = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map();
    for (const log of filteredLogs) {
      let key;
      switch (groupBy) {
        case "status":         key = log.status         || "Unknown"; break;
        case "module":         key = log.module          || "Unknown"; break;
        case "shopifyModule":  key = log.shopifyModule   || "Unknown"; break;
        case "date":           key = fmtDateKey(log.createdTime);     break;
        case "shop":           key = log.shop            || "Unknown"; break;
        default:               key = "All";
      }
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(log);
    }
    return [...map.entries()].map(([label, items]) => ({ label, items, count: items.length }));
  }, [filteredLogs, groupBy]);

  // ── Handlers ──

  const handleSort = (colKey) => {
    if (sortBy === colKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(colKey);
      setSortDir("asc");
    }
  };

  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  const handleDelete = async (logId) => {
    try {
      const ok = await deleteLogRecord(window.ZOHO, logId);
      if (ok) {
        setRawRecords((prev) => prev.filter((r) => (r.id || r.Id) !== logId));
        if (detailLog?.id === logId) setDetailLog(null);
      } else {
        setSnack?.({ open: true, message: "Failed to delete log.", severity: "error" });
      }
    } catch (err) {
      console.error("[Logs] delete error:", err);
      setSnack?.({ open: true, message: "Failed to delete log.", severity: "error" });
    }
  };

  const [retryingId, setRetryingId] = useState(null);

  const handleRetry = async (log) => {
    if (!log?.settingId || !log?.shopifyRecordId) {
      setSnack?.({ open: true, message: "Missing setting ID or Shopify ID.", severity: "error" });
      return;
    }
    setRetryingId(log.id);
    try {
      const headers = {
        orgid: orgId,
        apikey: apiKey,
        datacenterurl: dataCenterUrl,
        datacenter: dataCenter,
        shopifyshopurl: log.shop || "",
      };
      await retryWebhook(headers, log);
      setSnack?.({ open: true, message: "Retry triggered. Refresh logs to see the new entry.", severity: "success" });
      fetchLogs();
    } catch (err) {
      console.error("[Logs] retry error:", err);
      const msg = err?.response?.data?.error || err?.message || "Retry failed.";
      setSnack?.({ open: true, message: `Retry failed: ${msg}`, severity: "error" });
    }
    setRetryingId(null);
  };

  const clearAll = () => {
    setStatusFilter("");
    setModuleFilter("");
    setFromPlatform("");
    setToPlatform("");
    setSearchText("");
    setShopFilter("");
    setGroupBy("");
    setPage(1);
  };

  const activeFilterCount =
    (statusFilter ? 1 : 0) + (moduleFilter ? 1 : 0) +
    (fromPlatform ? 1 : 0) + (toPlatform ? 1 : 0) +
    (searchText ? 1 : 0) + (shopFilter ? 1 : 0);

  // ── Render helpers ──

  const renderStatusChip = (status) => {
    const c = STATUS_COLORS[status] || DEFAULT_STATUS;
    return (
      <Chip label={status} size="small" variant="outlined" sx={{
        bgcolor: c.bg, color: c.color, borderColor: c.border,
        borderWidth: "1.5px", fontWeight: 600, fontSize: 12, height: 22,
      }} />
    );
  };

  const renderModuleChip = (mod) => {
    const c = MODULE_COLORS[mod] || DEFAULT_MODULE;
    return (
      <Chip label={mod} size="small" sx={{
        bgcolor: c.bg, color: c.color, fontWeight: 500, fontSize: 12, height: 22,
      }} />
    );
  };

  const renderRow = (log) => (
    <TableRow
      key={log.id}
      hover
      sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
      onClick={() => setDetailLog(log)}
    >
      <TableCell sx={{ fontSize: 13, color: "#52525b", whiteSpace: "nowrap", py: 1.25 }}>
        <Box>{fmtDate(log.createdTime)}</Box>
        <Box sx={{ fontSize: 12, color: "#a1a1aa" }}>{fmtTime(log.createdTime)}</Box>
      </TableCell>
      <TableCell sx={{ py: 1.25 }}>
        <Tooltip title={log.name} arrow placement="top-start">
          <Box sx={{ fontFamily: "monospace", fontSize: 13, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {log.name || "—"}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ py: 1.25 }}>{log.module ? renderModuleChip(log.module) : "—"}</TableCell>
      <TableCell sx={{ fontSize: 13, py: 1.25 }}>{log.shopifyModule || "—"}</TableCell>
      <TableCell sx={{ fontSize: 13, fontFamily: "monospace", py: 1.25 }}>{log.shopifyRecordId || "—"}</TableCell>
      <TableCell sx={{ fontSize: 13, color: "#52525b", py: 1.25 }}>{log.shop || "—"}</TableCell>
      <TableCell sx={{ py: 1.25 }}>{log.status ? renderStatusChip(log.status) : "—"}</TableCell>
      <TableCell sx={{
        fontSize: 13, color: "#71717a", py: 1.25,
        maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        <Tooltip title={log.reason} arrow placement="top-start">
          <span>{log.reason || "—"}</span>
        </Tooltip>
      </TableCell>
      <TableCell
        align="right"
        sx={{
          py: 1.25,
          position: "sticky",
          right: 0,
          bgcolor: "#fff",
          zIndex: 1,
          borderLeft: "1px solid #f4f4f5",
          boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "4px" }}>
          {/* Retry only makes sense on Failed rows — hide it everywhere else.
              Disable + tooltip when the row is missing the IDs needed to
              actually re-fire the webhook. */}
          {log.status === "Failed" && (
            <Tooltip
              title={
                !log.settingId || !log.shopifyRecordId
                  ? "Missing setting ID or Shopify record ID — can't retry this log."
                  : "Retry this failed sync"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    retryingId === log.id ||
                    !log.settingId ||
                    !log.shopifyRecordId
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRetry(log);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    minWidth: 60,
                    px: 1,
                    py: 0.25,
                    borderColor: "#fecaca",
                    color: "#dc2626",
                    "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" },
                  }}
                >
                  {retryingId === log.id ? (
                    <CircularProgress size={12} sx={{ mr: 0.5, color: "#dc2626" }} />
                  ) : null}
                  Retry
                </Button>
              </span>
            </Tooltip>
          )}
          {log.shopifyUrl && (
            <Tooltip title="Open in Shopify">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(log.shopifyUrl, "_blank", "noopener"); }} sx={{ color: "#71717a" }}>
                <OpenInNewIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete log">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(log.id); }} sx={{ color: "#d4d4d8", "&:hover": { color: "#ef4444" } }}>
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderTableContent = (rows) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#fafafa" }}>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || "left"}
                sx={{
                  fontWeight: 600, fontSize: 12, color: "#71717a",
                  textTransform: "uppercase", letterSpacing: "0.04em",
                  py: 1, width: col.width, whiteSpace: "nowrap",
                  borderBottom: "1px solid #e4e4e7",
                  // Keep the Action column pinned to the right edge so the
                  // Retry button is always visible even when Reason text
                  // pushes the table wider than the viewport.
                  ...(col.key === "actions" && {
                    position: "sticky",
                    right: 0,
                    bgcolor: "#fafafa",
                    zIndex: 2,
                    borderLeft: "1px solid #e4e4e7",
                    boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.06)",
                  }),
                }}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : "asc"}
                    onClick={() => handleSort(col.key)}
                    sx={{ "& .MuiTableSortLabel-icon": { fontSize: 15 } }}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? rows.map(renderRow) : (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ fontSize: 14, color: "#a1a1aa" }}>
                  No logs match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ── Main render ──

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 18, fontWeight: 600, mb: "4px", letterSpacing: "-0.02em" }}>
            Logs
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#71717a" }}>
            View detailed sync activity logs and error reports.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
          onClick={fetchLogs}
          disabled={loading}
          sx={{ borderColor: "#e4e4e7", color: "#52525b", textTransform: "none", fontSize: 13 }}
        >
          Refresh
        </Button>
      </Box>

      {/* Filter bar */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Search logs..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#a1a1aa" }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />

        {/* Platform filters */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: 13 }}>From</InputLabel>
          <Select
            label="From"
            value={fromPlatform}
            onChange={(e) => { setFromPlatform(e.target.value); setPage(1); }}
            sx={{ fontSize: 13 }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>All</MenuItem>
            {PLATFORM_OPTIONS.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: 13 }}>To</InputLabel>
          <Select
            label="To"
            value={toPlatform}
            onChange={(e) => { setToPlatform(e.target.value); setPage(1); }}
            sx={{ fontSize: 13 }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>All</MenuItem>
            {PLATFORM_OPTIONS.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: 13 }}>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} sx={{ fontSize: 13 }}>
            <MenuItem value="" sx={{ fontSize: 13 }}>All</MenuItem>
            {statusOptions.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: 13 }}>Module</InputLabel>
          <Select label="Module" value={moduleFilter} onChange={(e) => { setModuleFilter(e.target.value); setPage(1); }} sx={{ fontSize: 13 }}>
            <MenuItem value="" sx={{ fontSize: 13 }}>All</MenuItem>
            {moduleOptions.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
          </Select>
        </FormControl>

        {shopOptions.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: 13 }}>Shop</InputLabel>
            <Select label="Shop" value={shopFilter} onChange={(e) => setShopFilter(e.target.value)} sx={{ fontSize: 13 }}>
              <MenuItem value="" sx={{ fontSize: 13 }}>All shops</MenuItem>
              {shopOptions.map((sh) => <MenuItem key={sh} value={sh} sx={{ fontSize: 13 }}>{sh}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: 13 }}>Group by</InputLabel>
          <Select label="Group by" value={groupBy} onChange={(e) => { setGroupBy(e.target.value); setCollapsedGroups(new Set()); }} sx={{ fontSize: 13 }}>
            <MenuItem value="" sx={{ fontSize: 13 }}>None</MenuItem>
            <MenuItem value="status" sx={{ fontSize: 13 }}>Status</MenuItem>
            <MenuItem value="module" sx={{ fontSize: 13 }}>Zoho Module</MenuItem>
            <MenuItem value="shopifyModule" sx={{ fontSize: 13 }}>Shopify Module</MenuItem>
            <MenuItem value="date" sx={{ fontSize: 13 }}>Date</MenuItem>
            <MenuItem value="shop" sx={{ fontSize: 13 }}>Shop</MenuItem>
          </Select>
        </FormControl>

        {activeFilterCount > 0 && (
          <Button size="small" onClick={clearAll} sx={{ textTransform: "none", fontSize: 13, color: "#b91c1c" }}>
            Clear ({activeFilterCount})
          </Button>
        )}

        <Typography sx={{ ml: "auto", fontSize: 13, color: "#a1a1aa", whiteSpace: "nowrap" }}>
          {filteredLogs.length} log{filteredLogs.length !== 1 ? "s" : ""}
          {moreRecords ? "+" : ""}
        </Typography>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Box sx={{
          p: 2, mb: 2, bgcolor: "#fef2f2", border: "1px solid #fecaca",
          borderRadius: "8px", fontSize: 14, color: "#991b1b",
        }}>
          {error}
        </Box>
      )}

      {/* Empty state */}
      {!loading && !error && logs.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8, color: "#71717a" }}>
          <Typography sx={{ fontSize: 40, mb: 1.5 }}>&#x1F4DD;</Typography>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 16, fontWeight: 600, color: "#09090b", mb: "6px" }}>
            No logs yet
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            Sync activity logs will appear here once syncing begins.
          </Typography>
        </Box>
      )}

      {/* Table */}
      {!loading && logs.length > 0 && (
        <Box sx={{ border: "1px solid #e4e4e7", borderRadius: "8px", overflow: "hidden", bgcolor: "#fff" }}>
          {!groups && renderTableContent(filteredLogs)}

          {groups && groups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.label);
            return (
              <Box key={group.label}>
                <Box
                  onClick={() => toggleGroup(group.label)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 2, py: 1, bgcolor: "#f9fafb",
                    borderBottom: "1px solid #e4e4e7",
                    cursor: "pointer", userSelect: "none",
                    "&:hover": { bgcolor: "#f4f4f5" },
                  }}
                >
                  {isCollapsed
                    ? <ExpandMoreIcon sx={{ fontSize: 18, color: "#71717a" }} />
                    : <ExpandLessIcon sx={{ fontSize: 18, color: "#71717a" }} />}
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{group.label}</Typography>
                  <Chip label={group.count} size="small" sx={{ height: 18, fontSize: 12, fontWeight: 600, bgcolor: "#e4e4e7" }} />
                </Box>
                {!isCollapsed && renderTableContent(group.items)}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {!loading && (page > 1 || moreRecords) && (
        <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: 13, color: "#71717a" }}>Page {page}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small" variant="outlined" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{ textTransform: "none", fontSize: 13, borderColor: "#e4e4e7", color: "#52525b" }}
            >
              Previous
            </Button>
            <Button
              size="small" variant="outlined" disabled={!moreRecords}
              onClick={() => setPage((p) => p + 1)}
              sx={{ textTransform: "none", fontSize: 13, borderColor: "#e4e4e7", color: "#52525b" }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Detail dialog */}
      <Dialog
        open={!!detailLog}
        onClose={() => setDetailLog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "10px" } }}
      >
        {detailLog && (
          <>
            <DialogTitle sx={{
              fontSize: 16, fontWeight: 600,
              borderBottom: "1px solid #f4f4f5",
              display: "flex", alignItems: "center", justifyContent: "space-between", pr: 2,
            }}>
              <span>Log Detail</span>
              {renderStatusChip(detailLog.status)}
            </DialogTitle>
            <DialogContent sx={{ pt: "16px !important" }}>
              {/* Meta grid */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2.5 }}>
                <DetailField label="Log Name" value={detailLog.name} mono />
                <DetailField label="Date" value={`${fmtDate(detailLog.createdTime)} ${fmtTime(detailLog.createdTime)}`} />
                <DetailField label="Zoho Module" value={detailLog.module} />
                <DetailField label="Module Record ID" value={detailLog.moduleRecordId} mono />
                <DetailField label="Shopify Module" value={detailLog.shopifyModule} />
                <DetailField label="Shopify Record ID" value={detailLog.shopifyRecordId} mono />
                <DetailField label="From" value={detailLog.fromPlatform} />
                <DetailField label="To" value={detailLog.toPlatform} />
                <DetailField label="Shop" value={detailLog.shop} />
                <DetailField label="Source" value={detailLog.source} />
                <DetailField label="Setting ID" value={detailLog.settingId} mono />
                {detailLog.shopifyUrl && (
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#71717a", mb: "3px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Shopify Link
                    </Typography>
                    <Typography
                      component="a" href={detailLog.shopifyUrl}
                      target="_blank" rel="noopener"
                      sx={{ fontSize: 14, color: "#1d4ed8", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                      {detailLog.shopifyUrl}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Reason */}
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#71717a", mb: "5px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Reason
                </Typography>
                <Box sx={{
                  p: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca",
                  borderRadius: "6px", fontSize: 14, color: "#991b1b",
                  fontFamily: "monospace", lineHeight: 1.6, wordBreak: "break-word", whiteSpace: "pre-wrap",
                }}>
                  {detailLog.reason || "—"}
                </Box>
              </Box>

              {/* Request body */}
              {detailLog.requestBody && (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#71717a", mb: "5px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Request Body
                  </Typography>
                  <Box sx={{
                    p: 1.5, bgcolor: "#fafafa", border: "1px solid #e4e4e7",
                    borderRadius: "6px", fontSize: 13, fontFamily: "monospace",
                    lineHeight: 1.5, maxHeight: 300, overflow: "auto",
                    wordBreak: "break-word", whiteSpace: "pre-wrap",
                  }}>
                    {(() => {
                      try { return JSON.stringify(JSON.parse(detailLog.requestBody), null, 2); }
                      catch { return detailLog.requestBody; }
                    })()}
                  </Box>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <Box>
      <Typography sx={{
        fontSize: 12, fontWeight: 600, color: "#71717a", mb: "3px",
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 14, color: "#09090b",
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-word",
      }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}
